import React from 'react'
import styled from 'styled-components'
import Head from 'next/head'
import Link from 'next/link'
import NdsThemeProvider from '@ntuctech/devex-tangram/Theme/NdsThemeProvider'
import Box from '@ntuctech/devex-tangram/Box'
import Typography from '@ntuctech/devex-tangram/Typography'
import ButtonContained from '@ntuctech/devex-tangram/Button/ButtonContained'
import SvgLinkPoints from '@ntuctech/devex-tangram/Icon/SvgLinkPoints'

import { Router } from '../../../routes'
import { SCREEN_SIZE } from '../../../lib/Media'
import {
  GTM_ENV_VAR as GEV,
  PAGE_TYPES,
} from '../../../lib/eventTrackerFunction'
import LocalStorageService from '../../../utils/LocalStorageService'
import { getExpConfig } from '../../../utils/configService'
import { SPLIT_FEATURES } from '../../../utils/constants'
import { DEFAULT_BENEFIT_CONFIGS } from '../../../utils/constants/general'
import { getUserType } from '../../../utils/userType'

import Layout from '../../Layout/Layout'
import Text from '../../Text'
import Image from '../../Image'
import BenefitNote from '../../BenefitNote'
import UnqualifiedCircularIcon from '../../icons/svg/unqualified-circular-icon.svg'
import QualifiedCircularIcon from '../../icons/svg/qualified-circular-icon.svg'
import ChevronLight from '../../icons/ChevronLight'
import FPDigitalClubLogo from '../../../static/FPDigitalClubLogo/image@4x.png'
import DigitalClubEntryPointImg from '../../../static/Membership/Registration/image.jpg'
import DigitalClubEntryPoint2xImg from '../../../static/Membership/Registration/image@2x.jpg'
import DigitalClubEntryPoint3xImg from '../../../static/Membership/Registration/image@3x.jpg'
import ProfileSwitchPopup from '../ProfileSwitchPopup'

const StyledLayout = styled(Layout)`
  & main {
    ${SCREEN_SIZE.Below.Tablet} {
      padding: 0;
    }
  }
`

const ContentLayer = styled.div`
  position: relative;
  color: #333333;
`

const ContentBodyWrapper = styled.div`
  margin-top: 1rem;
  .tangram-linkpoint-icon {
    height: 1.5rem;
    width: 1.5rem;
  }
`

const MiddleContent = styled.div`
  ${({ backgroundColor }) =>
    backgroundColor && `background-color: ${backgroundColor};`}
  text-align: center;
  padding: 0.5rem 0.5rem 1rem 0.5rem;
`

const HighlightedText = styled(Text)`
  margin-bottom: 0.5rem;
  text-align: center;
`

const BottomCard = styled.div`
  text-align: center;
  color: #333333;
  border-top: 0.0625rem solid #eaeaea;
  padding: 1.5rem 0;
  margin: 1.5rem 1.5rem 0;
`

const FeeText = styled(Text)`
  font-size: 2.25rem;
  margin: auto 0.15rem;
`

const Table = styled.div`
  margin: 0.5rem 0.5rem;
  ${SCREEN_SIZE.From.MobileL} {
    margin: 0.5rem 3.75rem;
  }
  position: relative;
`

const FirstColumn = styled.div`
  flex: 1 1 auto;
  min-height: 3.5rem;
  padding: 0.5rem 0.5rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
`

const SecondColumn = styled.div`
  ${SCREEN_SIZE.Only.Mobile} {
    width: 6.5rem;
  }
  width: 7.5rem;
  flex: 0 0 auto;
  min-height: 3.5rem;
  padding: 0 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const ThirdColumn = styled.div`
  ${SCREEN_SIZE.Only.Mobile} {
    width: 6.5rem;
  }
  width: 7.5rem;
  flex: 0 0 auto;
  min-height: 3.5rem;
  padding: 0.2rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const Row = styled.div`
  border-bottom: 0.0625rem solid #eaeaea;
  min-height: 3.5rem;
  width: 100%;
  display: flex;
  background-color: white;
  &:nth-child(even) {
    background-color: #f4f5f7;
  }
`

const TableNoteContainer = styled.div`
  display: flex;
  margin: 1rem auto 0;
  > div {
    margin: 0 auto;
  }
`

const HighlightedThirdColumnArea = styled.div`
  border: 0.125rem solid #1557bf;
  border-radius: 0.5rem;
  ${SCREEN_SIZE.Only.Mobile} {
    width: 6.5rem;
  }
  width: 7.5rem;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
`

const StyledLink = styled.a`
  text-decoration: none;
  color: #1557bf;
  font-size: 0.875rem;
  line-height: 1.25rem;
  cursor: pointer;
  &:visited {
    color: #1557bf;
  }
`

const FeeContainer = styled.div`
  margin-top: 1rem;
`

const LogoContainer = styled.div`
  margin: 0.625rem 0.8125rem 0.2rem;
`

const ReservedLogoSpace = styled.div`
  height: 2.1rem;
`

const FlexBox = styled.div`
  display: flex;
`

const PromoLink = styled.a`
  text-decoration: none;
  display: flex;

  .hide-mobile-text {
    ${SCREEN_SIZE.Only.Mobile} {
      display: none;
    }
  }

  svg {
    vertical-align: middle;
  }
`

const PromoLinkMobileText = styled.div`
  ${SCREEN_SIZE.Only.Mobile} {
    display: flex;
    flex-direction: column;
  }

  ${SCREEN_SIZE.From.MobileL} {
    display: none;
  }
`

const ButtonContainer = styled.div`
  margin: 1rem auto 0.5rem;
  width: 16.25rem;
`

const InfoCard = styled.div`
  background-color: white;
  max-width: 37.5rem;
  margin: 0 auto;
  position: relative;
`

const PageDescription = styled.div`
  ${SCREEN_SIZE.Only.Mobile} {
    margin: auto 0.5rem;
  }
`
const DC_TAGLINE_OLD =
  'Free delivery, zero service fee, lower prices and earn 2x Linkpoints when you shop online.'
const DC_TAGLINE_NEW = (
  <Box my={2}>
    <Typography variant="body2">
      Enjoy a free 1-month membership and exclusive benefits.
      <br />
      Pay just $9.99/month after your trial. For eligible customers only.
    </Typography>
  </Box>
)

const BenefitTable = () => {
  const dcBenefitDetailsSplit = getExpConfig(
    SPLIT_FEATURES.DC_FREE_TRIAL_BENEFITS
  )
  const dcBenefitDetails = JSON.parse(
    dcBenefitDetailsSplit?.config || JSON.stringify(DEFAULT_BENEFIT_CONFIGS)
  )
  const { dcLogo = FPDigitalClubLogo } = dcBenefitDetails

  return (
    <ContentBodyWrapper>
      <HighlightedText as="p" size="xl" color="#104393" weight="bold">
        What you will enjoy
      </HighlightedText>
      <Table>
        <Row>
          <FirstColumn />
          <SecondColumn>
            <LogoContainer>
              <ReservedLogoSpace />
            </LogoContainer>
            <Text as="p" weight="bold" margin="0 0 0.375rem 0">
              Non-member
            </Text>
          </SecondColumn>
          <ThirdColumn>
            <LogoContainer>
              <Image
                altText="FairPrice Digital Club Logo"
                src={dcLogo}
                width="100%"
                height="100%"
                noLazyLoading
              />
            </LogoContainer>
            <Text as="p" weight="black">
              Member
            </Text>
          </ThirdColumn>
        </Row>
        <Row>
          <FirstColumn>
            <Text as="p" weight="bold">
              Service fee
            </Text>
          </FirstColumn>
          <SecondColumn>
            <Text as="p" weight="black" size="large">
              $3.99
            </Text>
            <Text as="p" size="small">
              per order
            </Text>
          </SecondColumn>
          <ThirdColumn>
            <Text as="p" weight="black" size="large">
              Waived*
            </Text>
          </ThirdColumn>
        </Row>
        <Row>
          <FirstColumn>
            <Text as="p" weight="bold">
              Linkpoints
            </Text>
            <Text as="p" size="small">
              Earn 2X Linkpoints
            </Text>
          </FirstColumn>
          <SecondColumn>
            <FlexBox>
              <SvgLinkPoints
                className="tangram-linkpoint-icon"
                data-testid="linkpoint-icon"
              />
              <Text as="p" weight="black" size="large" margin="0 0 0 0.375rem">
                0.50
              </Text>
            </FlexBox>
            <Text as="p" size="small">
              every $1 spend
            </Text>
          </SecondColumn>
          <ThirdColumn>
            <FlexBox>
              <SvgLinkPoints
                className="tangram-linkpoint-icon"
                data-testid="linkpoint-icon"
              />
              <Text as="p" weight="black" size="large" margin="0 0 0 0.375rem">
                1
              </Text>
            </FlexBox>
            <Text as="p" size="small">
              every $1 spend
            </Text>
          </ThirdColumn>
        </Row>
        <Row>
          <FirstColumn>
            <Text as="p" weight="bold">
              {`Exclusive promotions`}
            </Text>
            <Text size="small">(Over $10 savings monthly)</Text>
            <Link href="/tag/digital-club-promos" passHref>
              <PromoLink data-testid="dc-promo-link" target="_blank">
                <Text color="#1557bf" size="small" margin="auto 0">
                  View all promos
                </Text>
                <ChevronLight />
              </PromoLink>
            </Link>
          </FirstColumn>
          <SecondColumn>
            <Image alt="Unqualified" src={UnqualifiedCircularIcon} />
          </SecondColumn>
          <ThirdColumn>
            <Image alt="Unqualified" src={QualifiedCircularIcon} />
          </ThirdColumn>
        </Row>
        <Row>
          <FirstColumn>
            <Text as="p" weight="bold">
              Lifestyle deals
            </Text>
            <Link href="/promo/digital-club-partner-deals" passHref>
              <PromoLink data-testid="dc-partner-deals-link" target="_blank">
                <Text
                  color="#1557bf"
                  size="small"
                  margin="auto 0"
                  className={'hide-mobile-text'}
                >
                  View our partner deals
                </Text>
                <ChevronLight className={'hide-mobile-text'} />
                <PromoLinkMobileText>
                  <Text color="#1557bf" size="small" margin="auto 0">
                    View our partner
                  </Text>
                  <Text color="#1557bf" size="small" margin="auto 0">
                    deals <ChevronLight />
                  </Text>
                </PromoLinkMobileText>
              </PromoLink>
            </Link>
          </FirstColumn>
          <SecondColumn>
            <Image alt="Unqualified" src={UnqualifiedCircularIcon} />
          </SecondColumn>
          <ThirdColumn>
            <Image alt="Unqualified" src={QualifiedCircularIcon} />
          </ThirdColumn>
        </Row>
        <HighlightedThirdColumnArea />
      </Table>
    </ContentBodyWrapper>
  )
}

const InvitedUserContent = ({
  signUp,
  pageTitle,
  pageDescText,
  ctaButtonText,
  ctaButtonInfoText,
  isUserEligibleForFreeTrial,
  isLoggedIn,
}) => (
  <MiddleContent backgroundColor="#CEE7F8">
    <Text as="p" size="xl" color="#104393" weight="bold" margin="1rem 0 0 0">
      {pageTitle}
    </Text>
    <PageDescription data-testid="dc-page-description">
      {pageDescText}
    </PageDescription>

    {isLoggedIn && !isUserEligibleForFreeTrial && (
      <FeeContainer>
        <Text size="mxl" color="#0D3578">
          Only
        </Text>
        <FeeText weight="bold" color="#0D3578">
          $9.99
        </FeeText>
        <Text size="mxl" color="#0D3578">
          /month
        </Text>
      </FeeContainer>
    )}
    <ButtonContainer>
      <ButtonContained
        compact
        size="m"
        fullWidth
        onClick={signUp}
        data-testid="register-button-above"
      >
        {ctaButtonText}
      </ButtonContained>
    </ButtonContainer>
    {ctaButtonInfoText}
  </MiddleContent>
)

class DigitalClubEntryPoint extends React.PureComponent {
  constructor() {
    super()
    const isB2BUser = getUserType() === 'B2B'
    this.state = {
      blueBackgroundSize: {},
      enableProfileSwitchPopup: isB2BUser,
    }
    this.trackEvent = this.trackEvent.bind(this)
  }

  trackEvent(action, label) {
    const { track } = this.props
    track({
      [GEV.EVENT_CAT]: 'Account-Membership',
      [GEV.EVENT_ACTION]: action,
      [GEV.EVENT_LABEL]: label,
      [GEV.MEMSH_TYPE]: 'DC member',
      [GEV.PAGE_TYPE]: PAGE_TYPES.MICRO,
      [GEV.SCREEN_NAME]: 'Micro-Membership_Digital_Club',
    })
  }

  componentDidMount() {
    const { getDcFreeTrialEligibility, isLoggedIn } = this.props
    if (isLoggedIn) {
      getDcFreeTrialEligibility().then(res => {
        this.setState({ isUserEligibleForFreeTrial: res })
      })
    }
    this.trackEvent('Landed', 'Digital_Club-Join_SignUp')

    const isRecentlyLoggedIn =
      LocalStorageService.getItem('login_user') === 'false' &&
      this.props.isLoggedIn

    if (isRecentlyLoggedIn) {
      Router.pushRoute('/membership/checkout')
      return
    }
    this.setState({
      blueBackgroundSize: {
        headerStandardHeight: '33rem',
        headerMobileHeight: '31.5rem',
      },
    })
  }

  signUp = buttonPosition => {
    const { isLoggedIn, login } = this.props

    if (!isLoggedIn) {
      login()
      return
    }
    if (buttonPosition === 'Top') {
      this.trackEvent('Click', 'Digital_Club-Join_SignUp_Top')
    } else {
      this.trackEvent('Click', 'Digital_Club-Join_SignUp_Bottom')
    }

    Router.pushRoute('/membership/checkout')
  }

  render() {
    const { organizationData, categoryList, remoteConfig } = this.props
    const { enableProfileSwitchPopup } = this.state

    const faqLink = remoteConfig?.faqLink?.defaultValue?.value || ''

    const dcBenefitDetailsSplit = getExpConfig(
      SPLIT_FEATURES.DC_FREE_TRIAL_BENEFITS
    )
    const dcBenefitDetails = JSON.parse(dcBenefitDetailsSplit?.config || '{}')
    const {
      promotionBanners = [
        DigitalClubEntryPointImg,
        DigitalClubEntryPoint2xImg,
        DigitalClubEntryPoint3xImg,
      ],
      nonMemberText,
    } = dcBenefitDetails

    const bannerSrcSet = `${promotionBanners[0]} 1x, ${
      promotionBanners[1]
    } 2x, ${promotionBanners[2]} 3x`
    const defaultBannerSrc = promotionBanners[1]

    // DC free trial not eligible
    let pageTitle =
      'Be a FairPrice Digital Club member to enjoy exclusive benefits'
    let pageDescText = (
      <Text as="p" size="large">
        {DC_TAGLINE_OLD}
      </Text>
    )
    let ctaButtonText = 'Join the club'
    let ctaButtonInfoText = nonMemberText

    // DC free trial flag on
    if (this.props.isLoggedIn) {
      if (this.state.isUserEligibleForFreeTrial) {
        pageTitle = 'Get a free Digital Club trial'
        pageDescText = DC_TAGLINE_NEW
        ctaButtonText = 'Start 1-month free trial'
        ctaButtonInfoText = ''
      } else {
        pageDescText = (
          <Box my={2} mx={6}>
            <Typography variant="body2">{DC_TAGLINE_OLD}</Typography>
          </Box>
        )
      }
    } else {
      pageTitle = 'Get a free Digital Club trial'
      pageDescText = DC_TAGLINE_NEW
      ctaButtonText = 'Log in to check eligibility'
      ctaButtonInfoText = ''
    }

    const innerContent = (
      <InfoCard>
        <ContentLayer>
          <Image
            altText="Membership Registration"
            srcSet={bannerSrcSet}
            src={defaultBannerSrc}
            width="100%"
            height="100%"
            noLazyLoading
          />
          <InvitedUserContent
            signUp={() => {
              this.signUp('Top')
            }}
            pageTitle={pageTitle}
            pageDescText={pageDescText}
            ctaButtonText={ctaButtonText}
            ctaButtonInfoText={ctaButtonInfoText}
            isUserEligibleForFreeTrial={this.state.isUserEligibleForFreeTrial}
            isLoggedIn={this.props.isLoggedIn}
          />
          <BenefitTable />
          <MiddleContent>
            <ButtonContainer>
              <ButtonContained
                compact
                size="m"
                fullWidth
                data-testid="register-button-below"
                onClick={() => {
                  this.signUp('Bottom')
                }}
              >
                {ctaButtonText}
              </ButtonContained>
            </ButtonContainer>
            {ctaButtonInfoText}
          </MiddleContent>
        </ContentLayer>
        <BottomCard>
          <Text as="p" size="xl" weight="bold" color="#104393">
            Need help?
          </Text>
          <StyledLink href={faqLink}>Check out our FAQs.</StyledLink>
          <TableNoteContainer>
            <BenefitNote />
          </TableNoteContainer>
        </BottomCard>
      </InfoCard>
    )

    return (
      <NdsThemeProvider>
        <Head>
          <meta
            name="description"
            key="meta_description"
            content="Enjoy Everyday Low Prices at Singapore's trusted grocery retailer. FairPrice offers a wide range of products with prices matched online and in stores."
          />
          <title>NTUC FairPrice | Membership Registration</title>
        </Head>
        {this.props.standAlonePage ? (
          <StyledLayout
            organizationData={organizationData}
            categoryList={categoryList}
            showPostalCodeModule
          >
            {innerContent}
          </StyledLayout>
        ) : (
          innerContent
        )}
        {enableProfileSwitchPopup && <ProfileSwitchPopup />}
      </NdsThemeProvider>
    )
  }
}

export default DigitalClubEntryPoint
